import { useMutation, useQueryClient } from "@tanstack/react-query";
import { membershipApi } from "@bw/shared/api";
import type { SubscriptionResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Undo a pending soft-cancel (POST /memberships/{id}/reactivate) — resumes the
 * subscription so it renews again. Only meaningful when cancel_at_period_end is
 * true. Both outcomes await list/detail/balance revalidation against the
 * server's actual state, never an optimistic local flip.
 *
 * Errors surface on `mutation.error` for the detail page to show honestly.
 */
export function useReactivateSubscription(subscriptionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<SubscriptionResponse, unknown, void>({
    mutationFn: () =>
      membershipApi.reactivateSubscription(subscriptionId as string),
    onSettled: () => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.memberships.mine() }),
      qc.invalidateQueries({ queryKey: queryKeys.memberships.detail(subscriptionId ?? "") }),
      qc.invalidateQueries({ queryKey: queryKeys.memberships.visitBalance(subscriptionId ?? "") }),
    ]),
  });
}
